import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  ArrowDownTrayIcon,
  CheckBadgeIcon,
  CalendarIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import confetti from 'canvas-confetti';

export default function CertificateView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchCertificate();
    // Fire confetti on load to celebrate
    setTimeout(() => {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }, 500);
  }, [id]);

  const fetchCertificate = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/certificates/${id}`);
      
      // The API returns the certificate data directly, not nested
      setCertificate(response.data);
    } catch (error) {
      console.error('Error fetching certificate:', error);
      if (error.response?.status === 404) {
        alert('Certificate not found');
        navigate('/my-certificates');
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadCertificate = async () => {
    try {
      setDownloading(true);
      const response = await api.get(`/certificates/${id}/download`, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate-${certificate.certificate_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Failed to download certificate');
    } finally {
      setDownloading(false);
    }
  };

  const viewCertificatePDF = async () => {
    try {
      const response = await api.get(`/certificates/${id}/stream`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing certificate:', error);
      alert('Failed to view certificate');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cte-blue"></div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Certificate not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/my-certificates')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          Back to Certificates
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Certificate</h1>
            <p className="text-gray-600 mt-1">
              {certificate.type === 'course_completion' ? 'Course Completion Certificate' : 'Module Certificate'}
            </p>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={viewCertificatePDF}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <AcademicCapIcon className="h-5 w-5" />
              View PDF
            </button>
            
            <button
              onClick={downloadCertificate}
              disabled={downloading}
              className="flex items-center gap-2 px-4 py-2 bg-cte-blue text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              {downloading ? 'Downloading...' : 'Download'}
            </button>
          </div>
        </div>
      </div>

      {/* Certificate Preview Card */}
      <div className="bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
        {/* Decorative Header */}
        <div className="bg-gradient-to-r from-cte-blue to-blue-600 px-8 py-6">
          <div className="flex items-center justify-center gap-3 text-white">
            <CheckBadgeIcon className="h-12 w-12" />
            <div className="text-center">
              <h2 className="text-2xl font-bold">Certificate of Achievement</h2>
              <p className="text-blue-100 text-sm mt-1">AI Prompting & Agent Systems</p>
            </div>
          </div>
        </div>

        {/* Certificate Content */}
        <div className="px-8 py-10 text-center">
          {/* Certificate Number */}
          <div className="mb-8">
            <p className="text-sm text-gray-500 uppercase tracking-wide mb-2">Certificate Number</p>
            <p className="text-2xl font-mono font-bold text-gray-900">{certificate.certificate_number}</p>
          </div>

          {/* Student Name */}
          <div className="mb-8">
            <p className="text-sm text-gray-500 uppercase tracking-wide mb-2">This certifies that</p>
            <p className="text-4xl font-bold text-gray-900">{certificate.user?.name || 'Student'}</p>
          </div>

          {/* Achievement */}
          <div className="mb-8">
            <p className="text-sm text-gray-500 uppercase tracking-wide mb-2">Has successfully completed</p>
            <p className="text-2xl font-semibold text-cte-blue">
              {certificate.type === 'course_completion' 
                ? 'The Complete AI Prompting & Agent Systems Course' 
                : certificate.module?.title || 'Module'}
            </p>
          </div>

          {/* Completion Details */}
          {certificate.metadata && (
            <div className="mb-8 bg-gray-50 rounded-lg px-6 py-4 max-w-2xl mx-auto">
              <div className="grid grid-cols-2 gap-6 text-left">
                {certificate.metadata.completion_percentage && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Completion</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {certificate.metadata.completion_percentage}%
                    </p>
                  </div>
                )}
                
                {certificate.metadata.modules_completed !== undefined && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Modules Completed</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {certificate.metadata.modules_completed}
                    </p>
                  </div>
                )}

                {certificate.metadata.labs_completed !== undefined && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Labs Completed</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {certificate.metadata.labs_completed}
                    </p>
                  </div>
                )}

                {certificate.metadata.capstones_completed !== undefined && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Capstones Completed</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {certificate.metadata.capstones_completed}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Issue Date */}
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <CalendarIcon className="h-5 w-5" />
            <p>
              Issued on {new Date(certificate.issued_at || certificate.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>

        {/* Decorative Footer */}
        <div className="bg-gray-50 px-8 py-4 border-t border-gray-200">
          <p className="text-center text-sm text-gray-500">
            This certificate verifies completion of course requirements and demonstrates
            proficiency in AI prompting techniques and agent system development.
          </p>
        </div>
      </div>

      {/* Additional Actions */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Share your achievement!</strong> Add this certificate to your LinkedIn profile, 
          resume, or portfolio to showcase your AI expertise.
        </p>
      </div>
    </div>
  );
}
